import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../../config";

const fmtDur = (s) => (s === null || s === undefined ? "—" : `${Math.floor(s / 60)}m${String(Math.round(s % 60)).padStart(2, "0")}s (${Math.round(s)} s)`);

/** Admin → Podcast → TTS Lab: render any episode script with the configured (or custom) voice/model WITHOUT
 *  touching the episode or Spreaker; shows per-render metrics (blocks, duration, truncation, junction quality,
 *  request-ids) and issues expiring share links. Used for the production W43 cut-over check (iter161). */
export const PodcastTtsLabPanel = ({ episodes, config, headers, dm, c, btnPrimary, btnSecondary, inputCls, labelCls }) => {
  const eff = config?.tts_effective || {};
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [form, setForm] = useState({ episode_id: "", model: "", voice_id: "", block_chars: "", label: "" });
  const [shared, setShared] = useState({});

  useEffect(() => {
    setForm((f) => ({ ...f, model: f.model || eff.model || config?.elevenlabs_model || "eleven_v4",
      voice_id: f.voice_id || eff.voice_id || config?.elevenlabs_voice_id || "",
      block_chars: f.block_chars || (eff.block_chars ? String(eff.block_chars) : ""),
      episode_id: f.episode_id || (episodes.find((e) => e.status === "published") || episodes[0] || {}).id || "" }));
  }, [eff.model, eff.voice_id, eff.block_chars, config, episodes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/podcast/tts-test?limit=12`, { headers });
      setTests(data.tests || []);
    } catch { /* keep the previous list */ } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!tests.some((t) => t.status === "running")) return undefined;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [tests, load]);

  const start = async () => {
    if (!form.episode_id) return toast.error("Pick an episode");
    setStarting(true);
    try {
      const ep = episodes.find((e) => e.id === form.episode_id);
      const body = { episode_id: form.episode_id, model: form.model, voice_id: form.voice_id || undefined,
        block_chars: form.block_chars ? parseInt(form.block_chars, 10) : undefined, week_id: ep?.week || undefined,
        label: form.label || `${ep?.week || ""} ${form.model} ${form.voice_id.slice(0, 6)} b${form.block_chars || "cfg"}`.trim() };
      const { data } = await axios.post(`${API}/admin/podcast/tts-test`, body, { headers });
      toast.success(`Render started (${data.script_chars.toLocaleString()} chars) — ElevenLabs is billed for this run`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the render");
    } finally { setStarting(false); }
  };

  const share = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/podcast/tts-test/${id}/share?hours=120`, {}, { headers });
      const url = `${window.location.origin}${data.path}`;
      setShared((s) => ({ ...s, [id]: { url, expires_at: data.expires_at } }));
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied (valid 5 days)"); } catch { toast.success("Share link ready below"); }
    } catch { toast.error("Could not create the share link"); }
  };

  const sel = `${inputCls} text-xs`;
  return (
    <div className="space-y-4" data-testid="podcast-tts-lab">
      <div className={`rounded-xl border p-4 ${c.card}`}>
        <h3 className={`text-sm font-semibold ${c.text}`}>TTS Lab — render without publishing</h3>
        <p className={`text-xs mt-1 ${c.muted}`}>
          Effective production preset: voice <code>{eff.voice_id}</code> · {eff.model} · blocks {eff.block_chars ?? "—"} · stability {eff.settings?.stability ?? "—"} ·
          dictionary {eff.pronunciation_dictionary_locators?.[0]?.pronunciation_dictionary_id || "—"} · fallback {eff.fallback_model || "—"}
          {eff.preset_version && <> · preset {eff.preset_version}</>}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-3">
          <label className={`${labelCls} md:col-span-2`}>Episode
            <select className={sel} value={form.episode_id} onChange={(e) => setForm({ ...form, episode_id: e.target.value })} data-testid="tts-lab-episode">
              {episodes.map((e) => <option key={e.id} value={e.id}>{e.week || "—"} · {(e.title || e.id).slice(0, 70)} [{e.status}]</option>)}
            </select>
          </label>
          <label className={labelCls}>Model
            <select className={sel} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="tts-lab-model">
              {["eleven_v4", "eleven_v4_hq", "eleven_v4_turbo", "eleven_multilingual_v2", "eleven_v3"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className={labelCls}>Voice ID
            <input className={sel} value={form.voice_id} onChange={(e) => setForm({ ...form, voice_id: e.target.value })} data-testid="tts-lab-voice" />
          </label>
          <label className={labelCls}>Block chars
            <input className={sel} type="number" min="500" max="9500" step="100" value={form.block_chars} onChange={(e) => setForm({ ...form, block_chars: e.target.value })} data-testid="tts-lab-blocks" />
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <label className={`${labelCls} flex-1 min-w-[220px]`}>Label
            <input className={sel} value={form.label} placeholder="W43 prod cut-over check" onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="tts-lab-label" />
          </label>
          <button type="button" className={btnPrimary} onClick={start} disabled={starting || !form.episode_id} data-testid="tts-lab-start">
            {starting ? "Starting…" : "Render test"}
          </button>
          <button type="button" className={btnSecondary} onClick={load} disabled={loading} data-testid="tts-lab-refresh">Refresh</button>
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${c.card}`} data-testid="tts-lab-list">
        <h3 className={`text-sm font-semibold mb-3 ${c.text}`}>Renders</h3>
        {tests.length === 0 && <p className={`text-xs ${c.muted}`}>No renders yet.</p>}
        <ul className="space-y-3">
          {tests.map((t) => (
            <li key={t.id} className={`rounded-lg border p-3 text-xs ${dm ? "border-white/10" : "border-black/10"}`} data-testid={`tts-lab-test-${t.id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className={`font-semibold ${c.text}`}>{t.label || t.id}</span>
                <span className={`px-1.5 py-0.5 rounded ${t.status === "completed" ? "bg-emerald-500/15 text-emerald-500" : t.status === "failed" ? "bg-red-500/15 text-red-500" : "bg-amber-500/15 text-amber-500"}`} data-testid={`tts-lab-status-${t.id}`}>{t.status}</span>
                <span className={c.muted}>{t.tts_model || t.model} · {t.tts_voice_id || t.voice_id} · blocks ≤{t.tts_block_chars || t.block_chars || "cfg"} · seed {t.tts_seed ?? "—"}</span>
                <span className={`ml-auto ${c.muted}`}>{t.started_at ? new Date(t.started_at).toLocaleString() : ""}</span>
              </div>
              {t.status === "completed" && (
                <dl className="grid grid-cols-2 md:grid-cols-6 gap-x-4 gap-y-1 mt-2 tabular-nums">
                  <dt className={c.muted}>Duration</dt><dd className={c.text} data-testid={`tts-lab-duration-${t.id}`}>{fmtDur(t.tts_duration_s)}</dd>
                  <dt className={c.muted}>Blocks</dt><dd className={c.text}>{t.planned_blocks ?? (t.tts_segments || []).length}</dd>
                  <dt className={c.muted}>Truncated</dt><dd className={t.tts_truncated_chars ? "text-red-500" : c.text}>{t.tts_truncated_chars ?? "—"} chars</dd>
                  <dt className={c.muted}>Billed</dt><dd className={c.text}>{(t.tts_cost_chars || 0).toLocaleString()} chars · {t.chars_per_second ?? "—"} ch/s</dd>
                  <dt className={c.muted}>Max |Δ| at cuts</dt><dd className={c.text}>{t.tts_join_max_delta ?? "n/a"} · gap ≤ {t.tts_join_max_gap_ms ?? "—"} ms</dd>
                  <dt className={c.muted}>Fallback</dt><dd className={t.tts_fallback_used ? "text-amber-500" : c.text}>{t.tts_fallback_used ? `yes (${t.tts_fallback_reason || "?"})` : "no"}</dd>
                </dl>
              )}
              {t.status === "completed" && (t.tts_segments || []).length > 0 && (
                <details className="mt-2">
                  <summary className={`cursor-pointer ${c.muted}`}>Segment log</summary>
                  <table className="w-full mt-1 tabular-nums">
                    <thead><tr className={c.muted}><th className="text-left">#</th><th className="text-right">chars</th><th className="text-left pl-3">request_id</th><th className="text-right">duration</th><th className="text-right">Δ at cut</th><th className="text-right">ΔRMS dB</th><th className="text-right">gap ms</th></tr></thead>
                    <tbody>
                      {t.tts_segments.map((s) => (
                        <tr key={s.index} className={c.text}>
                          <td>{s.index}</td><td className="text-right">{s.chars}</td><td className="pl-3 font-mono">{s.request_id || "—"}</td>
                          <td className="text-right">{(s.decoded_duration_s ?? s.duration_s)?.toFixed?.(1)} s</td>
                          <td className="text-right">{s.join ? s.join.max_delta_at_cut : "—"}</td><td className="text-right">{s.join ? s.join.rms_delta_db : "—"}</td><td className="text-right">{s.join ? s.join.gap_ms : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
              {t.status === "failed" && <p className="text-red-500 mt-1">{t.error}</p>}
              {t.status === "completed" && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <a className={btnSecondary} href={`${API}/admin/podcast/tts-test/${t.id}/audio`} target="_blank" rel="noreferrer" data-testid={`tts-lab-listen-${t.id}`}>Listen</a>
                  <button type="button" className={btnSecondary} onClick={() => share(t.id)} data-testid={`tts-lab-share-${t.id}`}>Share link (5 days)</button>
                  {shared[t.id] && <code className={`text-[11px] break-all ${c.muted}`} data-testid={`tts-lab-share-url-${t.id}`}>{shared[t.id].url}</code>}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
